import { useState } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { api, formatApiError } from "@/lib/api";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { toast } from "sonner";

import { Eye, Save, X } from "lucide-react";

export default function AdminTrendingPopup() {
  const qc = useQueryClient();

  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(false);

  /* =========================================================
     LOAD TRENDING POPUP SETTINGS
  ========================================================= */

  const {
    data,
    isLoading: popupLoading,
  } = useQuery({
    queryKey: ["admin-trending"],
    queryFn: () =>
      api
        .get("/trending-popup")
        .then((r) => r.data),
  });

  /* =========================================================
     LOAD PACKAGES
  ========================================================= */

  const {
    data: pkgs,
    isLoading: packagesLoading,
  } = useQuery({
    queryKey: ["all-packages"],
    queryFn: () =>
      api
        .get("/admin/packages", {
          params: {
            limit: 200,
          },
        })
        .then((r) => r.data),
  });

  /* =========================================================
     DEFAULT FORM
  ========================================================= */

  const defaultForm = {
    enabled: false,
    package_id: "",
    badge_text: "🔥 Trending Package",
    custom_title: "",
    custom_description: "",
    primary_cta_label: "Book Now",
    secondary_cta_label: "View Package",
    delay_seconds: 3,
    frequency: "session",
    start_date: "",
    end_date: "",
    device_target: "both",
    page_target: "home",
  };

  const s =
    form ??
    data ??
    defaultForm;

  /* =========================================================
     UPDATE FIELD
  ========================================================= */

  const setF = (key, value) => {
    setForm({
      ...s,
      [key]: value,
    });
  };

  /* =========================================================
     SAVE
  ========================================================= */

  const save = useMutation({
    mutationFn: (payload) =>
      api
        .put("/admin/trending-popup", payload)
        .then((r) => r.data),

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin-trending"],
      });

      qc.invalidateQueries({
        queryKey: ["trending-popup"],
      });

      toast.success(
        "Popup settings saved."
      );
    },

    onError: (e) => {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        ) || "Save failed"
      );
    },
  });

  /* =========================================================
     PACKAGE LIST
  ========================================================= */

  const packageItems =
    pkgs?.items || [];

  /* =========================================================
     SELECTED PACKAGE
  ========================================================= */

  const selectedPkg =
    packageItems.find(
      (p) =>
        String(p.id) ===
        String(s.package_id)
    );

  /* =========================================================
     RESET OVERRIDES
  ========================================================= */

  const resetToPackage = () => {
    setForm({
      ...s,
      custom_title: "",
      custom_description: "",
    });
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (
    popupLoading ||
    packagesLoading
  ) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 rounded-lg bg-slate-200 animate-pulse" />

        <div className="h-24 rounded-xl bg-slate-200 animate-pulse" />

        <div className="h-72 rounded-xl bg-slate-200 animate-pulse" />
      </div>
    );
  }

  /* =========================================================
     PAGE
  ========================================================= */

  return (
    <div className="space-y-6">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600">
            Marketing
          </div>

          <h1 className="font-display text-3xl text-slate-950 mt-1">
            Trending Package Popup
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            Select a package and control how the trending popup
            appears on your website.
          </p>
        </div>

        <div className="flex items-center gap-2">

          <Button
            variant="outline"
            onClick={() => setPreview(true)}
            disabled={!selectedPkg}
            data-testid="popup-preview-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>

          <Button
            className="bg-brand-navy-900 hover:bg-brand-navy-800 text-white"
            onClick={() => save.mutate(s)}
            disabled={save.isPending}
            data-testid="popup-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />

            {save.isPending
              ? "Saving…"
              : "Save"}
          </Button>

        </div>
      </div>

      {/* =====================================================
          STATUS
      ===================================================== */}

      <Card className="border border-slate-200">
        <CardContent className="p-6">

          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

            <div className="flex flex-wrap items-center gap-5">

              <Badge
                className={
                  s.enabled
                    ? "bg-emerald-600"
                    : "bg-slate-500"
                }
              >
                {s.enabled
                  ? "🟢 Active"
                  : "⚪ Disabled"}
              </Badge>

              <div>
                <div className="text-slate-500 text-xs uppercase tracking-[0.2em]">
                  Trending package
                </div>

                <div className="text-slate-900 font-medium">
                  {selectedPkg?.name ||
                    "— not selected —"}
                </div>
              </div>

              <div>
                <div className="text-slate-500 text-xs uppercase tracking-[0.2em]">
                  Schedule
                </div>

                <div className="text-slate-900">
                  {s.start_date || "—"}
                  {" → "}
                  {s.end_date ||
                    "No end date"}
                </div>
              </div>

              <div>
                <div className="text-slate-500 text-xs uppercase tracking-[0.2em]">
                  Frequency
                </div>

                <div className="text-slate-900 capitalize">
                  {s.frequency ||
                    "session"}
                </div>
              </div>

              <div>
                <div className="text-slate-500 text-xs uppercase tracking-[0.2em]">
                  Target
                </div>

                <div className="text-slate-900 capitalize">
                  {s.page_target ||
                    "home"}
                  {" · "}
                  {s.device_target ||
                    "both"}
                </div>
              </div>

            </div>

            <div className="flex items-center gap-3">

              <Label className="text-sm">
                Show Popup
              </Label>

              <Switch
                checked={!!s.enabled}
                onCheckedChange={(value) =>
                  setF("enabled", value)
                }
                data-testid="popup-enabled"
              />

            </div>

          </div>

        </CardContent>
      </Card>

      {/* =====================================================
          PACKAGE SELECTION
      ===================================================== */}

      <Card className="border border-slate-200">

        <CardContent className="p-6 space-y-5">

          <div>
            <div className="text-sm font-medium text-slate-700">
              Trending Package
            </div>

            <p className="mt-1 text-xs text-slate-500">
              Select the package that should appear in the
              trending popup.
            </p>
          </div>

          <div>

            <Label>
              Select package
            </Label>

            <select
              className="mt-2 w-full h-11 border rounded-md px-3 bg-white border-slate-300 text-sm"
              value={s.package_id || ""}
              onChange={(e) =>
                setF(
                  "package_id",
                  e.target.value
                )
              }
              data-testid="popup-package"
            >

              <option value="">
                — Select a package —
              </option>

              {packageItems.map((p) => (
                <option
                  key={p.id}
                  value={p.id}
                >
                  {p.name} — {p.destination}
                </option>
              ))}

            </select>

          </div>

          {selectedPkg && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">

              <div className="flex items-center gap-4">

                {selectedPkg.banner_url && (
                  <img
                    src={selectedPkg.banner_url}
                    alt={selectedPkg.name}
                    className="h-16 w-24 rounded-lg object-cover"
                  />
                )}

                <div>

                  <div className="font-medium text-slate-900">
                    {selectedPkg.name}
                  </div>

                  <div className="text-sm text-slate-500">
                    {selectedPkg.destination}
                  </div>

                  <div className="text-xs text-slate-400 mt-1">
                    Popup image: Package banner
                  </div>

                </div>

              </div>

            </div>
          )}

        </CardContent>

      </Card>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <Card className="border border-slate-200">

        <CardContent className="p-6 space-y-5">

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

            <div>

              <div className="text-sm font-medium text-slate-700">
                Content
              </div>

              <div className="text-xs text-slate-500 mt-1">
                Optional text overrides. The popup image always
                comes from the selected package.
              </div>

            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={resetToPackage}
              data-testid="popup-reset"
            >
              Reset to package details
            </Button>

          </div>

          <div className="grid md:grid-cols-2 gap-5">

            {/* BADGE */}

            <div>

              <Label>
                Badge text
              </Label>

              <Input
                className="mt-2"
                value={s.badge_text || ""}
                onChange={(e) =>
                  setF(
                    "badge_text",
                    e.target.value
                  )
                }
                placeholder="🔥 Trending Package"
                data-testid="popup-badge"
              />

            </div>

            {/* TITLE */}

            <div>

              <Label>
                Popup title
              </Label>

              <Input
                className="mt-2"
                value={s.custom_title || ""}
                onChange={(e) =>
                  setF(
                    "custom_title",
                    e.target.value
                  )
                }
                placeholder="Leave blank = package name"
                data-testid="popup-title"
              />

            </div>

            {/* DESCRIPTION */}

            <div className="md:col-span-2">

              <Label>
                Description
              </Label>

              <Textarea
                className="mt-2"
                rows={4}
                value={
                  s.custom_description || ""
                }
                onChange={(e) =>
                  setF(
                    "custom_description",
                    e.target.value
                  )
                }
                placeholder="Leave blank = package overview"
                data-testid="popup-desc"
              />

            </div>

          </div>

        </CardContent>

      </Card>

      {/* =====================================================
          CTA SETTINGS
      ===================================================== */}

      <Card className="border border-slate-200">

        <CardContent className="p-6 space-y-5">

          <div className="text-sm font-medium text-slate-700">
            CTA Buttons
          </div>

          <div className="grid md:grid-cols-2 gap-5">

            <div>

              <Label>
                Primary button
              </Label>

              <select
                className="mt-2 w-full h-11 border rounded-md px-3 bg-white border-slate-300 text-sm"
                value={
                  s.primary_cta_label ||
                  "Book Now"
                }
                onChange={(e) =>
                  setF(
                    "primary_cta_label",
                    e.target.value
                  )
                }
                data-testid="popup-cta-primary"
              >

                <option>
                  Book Now
                </option>

                <option>
                  View Package
                </option>

                <option>
                  Enquire Now
                </option>

              </select>

            </div>

            <div>

              <Label>
                Secondary button
              </Label>

              <select
                className="mt-2 w-full h-11 border rounded-md px-3 bg-white border-slate-300 text-sm"
                value={
                  s.secondary_cta_label ||
                  "View Package"
                }
                onChange={(e) =>
                  setF(
                    "secondary_cta_label",
                    e.target.value
                  )
                }
                data-testid="popup-cta-secondary"
              >

                <option>
                  View Package
                </option>

                <option>
                  Contact Us
                </option>

                <option value="none">
                  Hide secondary
                </option>

              </select>

            </div>

          </div>

        </CardContent>

      </Card>

      {/* =====================================================
          BEHAVIOUR
      ===================================================== */}

      <Card className="border border-slate-200">

        <CardContent className="p-6 space-y-5">

          <div>

            <div className="text-sm font-medium text-slate-700">
              Behaviour
            </div>

            <p className="mt-1 text-xs text-slate-500">
              Control when and where the popup appears.
            </p>

          </div>

          <div className="grid md:grid-cols-4 gap-4">

            {/* DELAY */}

            <div>

              <Label>
                Delay (seconds)
              </Label>

              <Input
                className="mt-2"
                type="number"
                min="0"
                value={
                  s.delay_seconds ?? 3
                }
                onChange={(e) =>
                  setF(
                    "delay_seconds",
                    Number(e.target.value)
                  )
                }
                data-testid="popup-delay"
              />

            </div>

            {/* FREQUENCY */}

            <div>

              <Label>
                Frequency
              </Label>

              <select
                className="mt-2 w-full h-11 border rounded-md px-3 bg-white border-slate-300 text-sm"
                value={
                  s.frequency ||
                  "session"
                }
                onChange={(e) =>
                  setF(
                    "frequency",
                    e.target.value
                  )
                }
                data-testid="popup-frequency"
              >

                <option value="session">
                  Once per session
                </option>

                <option value="daily">
                  Once per day
                </option>

                <option value="always">
                  Every visit
                </option>

              </select>

            </div>

            {/* PAGE */}

            <div>

              <Label>
                Page target
              </Label>

              <select
                className="mt-2 w-full h-11 border rounded-md px-3 bg-white border-slate-300 text-sm"
                value={
                  s.page_target ||
                  "home"
                }
                onChange={(e) =>
                  setF(
                    "page_target",
                    e.target.value
                  )
                }
                data-testid="popup-page"
              >

                <option value="home">
                  Homepage only
                </option>

                <option value="all">
                  All public pages
                </option>

              </select>

            </div>

            {/* DEVICE */}

            <div>

              <Label>
                Device target
              </Label>

              <select
                className="mt-2 w-full h-11 border rounded-md px-3 bg-white border-slate-300 text-sm"
                value={
                  s.device_target ||
                  "both"
                }
                onChange={(e) =>
                  setF(
                    "device_target",
                    e.target.value
                  )
                }
                data-testid="popup-device"
              >

                <option value="both">
                  Desktop + Mobile
                </option>

                <option value="desktop">
                  Desktop only
                </option>

                <option value="mobile">
                  Mobile only
                </option>

              </select>

            </div>

          </div>

          {/* DATES */}

          <div className="grid md:grid-cols-2 gap-4">

            <div>

              <Label>
                Start date
              </Label>

              <Input
                className="mt-2"
                type="date"
                value={
                  s.start_date || ""
                }
                onChange={(e) =>
                  setF(
                    "start_date",
                    e.target.value
                  )
                }
                data-testid="popup-start"
              />

            </div>

            <div>

              <Label>
                End date
              </Label>

              <Input
                className="mt-2"
                type="date"
                value={
                  s.end_date || ""
                }
                onChange={(e) =>
                  setF(
                    "end_date",
                    e.target.value
                  )
                }
                data-testid="popup-end"
              />

              <p className="mt-1 text-xs text-slate-500">
                Leave blank for no end date.
              </p>

            </div>

          </div>

        </CardContent>

      </Card>

      {/* =====================================================
          POPUP PREVIEW
      ===================================================== */}

      <Card className="border border-slate-200">

        <CardContent className="p-6">

          <div className="flex items-center justify-between gap-4">

            <div>

              <div className="text-sm font-medium text-slate-700">
                Popup Preview
              </div>

              <p className="mt-1 text-xs text-slate-500">
                The preview uses only the selected package banner.
              </p>

            </div>

            <Button
              variant="outline"
              onClick={() => setPreview(true)}
              disabled={!selectedPkg}
            >
              <Eye className="w-4 h-4 mr-2" />
              Open Preview
            </Button>

          </div>

          {selectedPkg && (
            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">

              <div className="grid md:grid-cols-2">

                {/* PACKAGE IMAGE ONLY */}

                <div className="relative h-64 md:h-80 bg-slate-100">

                  {selectedPkg.banner_url ? (

                    <img
                      src={selectedPkg.banner_url}
                      alt={selectedPkg.name}
                      className="absolute inset-0 h-full w-full object-cover"
                    />

                  ) : (

                    <div className="absolute inset-0 flex items-center justify-center bg-slate-200">
                      <span className="text-sm text-slate-500">
                        No package image available
                      </span>
                    </div>

                  )}

                </div>

                {/* CONTENT */}

                <div className="p-7">

                  <div className="inline-flex rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-slate-950">
                    {s.badge_text ||
                      "🔥 Trending Package"}
                  </div>

                  <h3 className="mt-4 font-display text-2xl text-slate-950">
                    {s.custom_title ||
                      selectedPkg.name}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-600 line-clamp-4">
                    {s.custom_description ||
                      selectedPkg.overview ||
                      "Discover this tour package."}
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3">

                    <Button className="rounded-full bg-brand-navy-900 text-white hover:bg-brand-navy-800">
                      {s.primary_cta_label ||
                        "Book Now"}
                    </Button>

                    {s.secondary_cta_label !==
                      "none" && (
                      <Button
                        variant="outline"
                        className="rounded-full"
                      >
                        {s.secondary_cta_label ||
                          "View Package"}
                      </Button>
                    )}

                  </div>

                </div>

              </div>

            </div>
          )}

        </CardContent>

      </Card>

      {/* =====================================================
          FULL POPUP PREVIEW
      ===================================================== */}

      {selectedPkg &&
        preview && (
          <TrendingPopupPreview
            cfg={{
              ...s,
              package: selectedPkg,
            }}
            onClose={() =>
              setPreview(false)
            }
          />
        )}

    </div>
  );
}

/* ===========================================================
   FULL POPUP PREVIEW
=========================================================== */

function TrendingPopupPreview({
  cfg,
  onClose,
}) {
  const pkg = cfg.package;

  /*
    IMPORTANT:
    Only the package banner is used.
    No custom image.
    No gallery image.
  */

  const image =
    pkg?.banner_url || "";

  const title =
    cfg.custom_title ||
    pkg?.name ||
    "Trending Package";

  const description =
    cfg.custom_description ||
    pkg?.overview ||
    "Discover this tour package.";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">

      {/* BACKDROP */}

      <button
        type="button"
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close preview"
      />

      {/* POPUP */}

      <div
        className="relative z-10 w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* CLOSE */}

        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-md hover:text-slate-950"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="grid md:grid-cols-2">

          {/* =================================================
              PACKAGE IMAGE ONLY
          ================================================= */}

          <div className="relative h-[280px] sm:h-[360px] md:h-[520px] overflow-hidden bg-slate-100">

            {image ? (

              <img
                src={image}
                alt={pkg?.name || "Package"}
                className="absolute inset-0 h-full w-full object-cover"
              />

            ) : (

              <div className="absolute inset-0 flex items-center justify-center bg-slate-200">
                <span className="text-sm text-slate-500">
                  No package image available
                </span>
              </div>

            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

            {/* BADGE */}

            <div className="absolute left-4 top-4 sm:left-6 sm:top-6">

              <div className="inline-flex items-center rounded-full bg-amber-400 px-4 py-2 text-xs font-bold text-slate-950 shadow-lg">

                {cfg.badge_text ||
                  "🔥 Trending Package"}

              </div>

            </div>

          </div>

          {/* =================================================
              POPUP CONTENT
          ================================================= */}

          <div className="flex flex-col justify-center p-7 sm:p-9 md:p-10">

            <div className="text-xs uppercase tracking-[0.2em] text-amber-600">
              {pkg?.destination || ""}
            </div>

            <h3 className="mt-3 font-display text-3xl leading-tight text-slate-950 sm:text-4xl">
              {title}
            </h3>

            <p className="mt-4 text-sm leading-7 text-slate-600">
              {description}
            </p>

            <div className="mt-7 flex flex-wrap gap-3">

              <Button className="rounded-full bg-brand-navy-900 px-6 text-white hover:bg-brand-navy-800">
                {cfg.primary_cta_label ||
                  "Book Now"}
              </Button>

              {cfg.secondary_cta_label !==
                "none" && (
                <Button
                  variant="outline"
                  className="rounded-full px-6"
                >
                  {cfg.secondary_cta_label ||
                    "View Package"}
                </Button>
              )}

            </div>

            <div className="mt-6 text-xs text-slate-400">
              Preview only. Save to publish.
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}